import type * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as organizations from '../../../organizations'
import type * as recordAssignedOrgs from '../../record-assigned-orgs'
import type * as recordLabAssignments from '../../record-lab-assignments'
import type { Collection } from '../collection'
import * as plugins from '../plugins'
import type { TypeParams } from '../types'

/**
 * When lab-assignment creation begins (the assigned collection's `isCreating`
 * transitions to true), resets the browsable list to its initial state:
 * clears the space-dimension selection, releases any stale lab selection, and
 * pre-populates the department filter with the departments occupying the
 * current record so the list starts scoped to the record's own department(s).
 *
 * The reset writes the initial filters to the edited params so it flows through
 * the same apply cycle as user changes and coexists with the space-dimension
 * filter (a single apply / list reload).
 */
export const resetFiltersOnModalOpen = store.effects.create(
  (
    _: ec.Collection<TypeParams>['feature'],
    collection: Collection,
    recordAssignedOrgsEc: recordAssignedOrgs.Collection,
  ) =>
    rxjs
      .combineLatest([
        collection.plugins
          .lookup(plugins.spaceDimensionFilter.Plugin)
          .getWhenReady$(),
        collection.plugins
          .lookup(plugins.responsiblePersonFilter.Plugin)
          .getWhenReady$(),
        ec.models.CollectionProducer.getEc$<recordLabAssignments.TypeParams>(
          ec.Name.Predefined.SpaceLabAssignment,
          { waitUntilReady: true },
        ),
      ])
      .pipe(
        rxjs.switchMap(
          ([
            spaceDimensionFilterPlugin,
            responsiblePersonFilterPlugin,
            labAssignmentsEc,
          ]) =>
            labAssignmentsEc.plugins.currentEntityPlugin.feature.selectors
              .isCreating()
              .asObservable.pipe(
                rxjs.distinctUntilChanged(),
                rxjs.filter(Boolean),
                rxjs.withLatestFrom(
                  recordAssignedOrgsEc.plugins.currentListPlugin.feature
                    .selectors.entities.asObservable,
                ),
                rxjs.mergeMap(([, assignments]) => {
                  const occupierDepartmentIds = [
                    ...new Set(
                      assignments
                        .filter(({ isOccupier }) => isOccupier)
                        .map(
                          ({ organizationLookup }) =>
                            organizationLookup[
                              organizations.constants.DEPARTMENT_ENTITY_ID
                            ],
                        ),
                    ),
                  ].filter(TypeChecker.isNotNullish.raw)

                  const filters: ents.extra.space.Record.Filter =
                    occupierDepartmentIds.length
                      ? {
                          orgFilter: {
                            entityId:
                              organizations.constants.DEPARTMENT_ENTITY_ID,
                            recordIds: occupierDepartmentIds,
                          },
                        }
                      : {}

                  return [
                    spaceDimensionFilterPlugin.actions.resetSelections
                      .from('resetFiltersOnModalOpen (labs)')
                      .create(),
                    responsiblePersonFilterPlugin.actions.resetSelections
                      .from('resetFiltersOnModalOpen (labs)')
                      .create(),
                    collection.plugins.selectionPlugin.applied.actions.putInSelectionLookup
                      .from('resetFiltersOnModalOpen (labs)')
                      .create({
                        key: labAssignmentsEc.associations?.selectionTarget,
                        value: { release: true },
                      }),
                    // Set the initial filters on the edited draft; applying it
                    // clears any stale space filter and scopes the list to the
                    // pre-populated department in a single reload.
                    collection.plugins.currentListPlugin.editedParams.actions.setFilters
                      .from('resetFiltersOnModalOpen (labs)')
                      .create(filters),
                  ]
                }),
              ),
        ),
      ),
)
